import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { getViteEnv } from 'test/viteEnv';

import { getClientIpAddress } from 'utils/getClientIpAddress';

import type {
  CognitoInitiateAuthRequest,
  CognitoInitiateAuthSuccessResponse,
  CognitoClientMetadata,
  CognitoChallengeResponse,
  CognitoErrorResponse,
  CognitoRespondToAuthChallengeRequest,
  InitiateAuthResult,
  AuthResult,
  AuthChallengeResult,
} from 'services/auth/types';

import { handleSessionExpired } from './terminateSession';

const env = getViteEnv();
const COGNITO_REGION = env.VITE_COGNITO_REGION ?? 'us-east-1';
const COGNITO_CLIENT_ID = env.VITE_COGNITO_CLIENT_ID ?? '';
const COGNITO_USER_POOL_ID = env.VITE_COGNITO_USER_POOL_ID ?? '';

const COGNITO_INITIATE_AUTH_TARGET = 'AWSCognitoIdentityProviderService.InitiateAuth';
const COGNITO_RESPOND_TO_AUTH_CHALLENGE_TARGET =
  'AWSCognitoIdentityProviderService.RespondToAuthChallenge';
const AUTH_SCOPE = 'openid profile email https://api.spence.com/auth.read';
const COGNITO_REFRESH_AUTH = 'REFRESH_TOKEN_AUTH';

function getCognitoUrl(): string {
  return `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;
}

function getRequiredClientId(): string {
  if (!COGNITO_CLIENT_ID) {
    throw new Error(
      'Sign-in is temporarily unavailable. Please try again later or contact support.'
    );
  }

  return COGNITO_CLIENT_ID;
}

function getCognitoErrorMessage(
  err: CognitoErrorResponse,
  response: Response,
  fallbackMessage: string
): string {
  const errorMessage =
    typeof err?.message === 'string' && err.message.trim().length > 0 ? err.message.trim() : '';
  const statusText =
    typeof response.statusText === 'string' && response.statusText.trim().length > 0
      ? response.statusText.trim()
      : '';

  return errorMessage || statusText || fallbackMessage;
}

/** Cognito REFRESH_TOKEN_AUTH: invalid/expired refresh — clear session and send user to sign-in. */
function isInvalidRefreshTokenResponse(data: unknown): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const err = data as CognitoErrorResponse;
  const type = typeof err.__type === 'string' ? err.__type.trim() : '';
  const msg = typeof err.message === 'string' ? err.message.toLowerCase() : '';

  if (!msg.includes('invalid refresh token')) {
    return false;
  }

  return type === 'NotAuthorizedException' || type === '';
}

function isCognitoChallengeResponse(value: unknown): value is CognitoChallengeResponse {
  if (!value || typeof value !== 'object') return false;
  const maybe = value as Partial<CognitoChallengeResponse>;
  return typeof maybe.ChallengeName === 'string' && maybe.ChallengeName.trim().length > 0;
}

function buildAuthChallengeResult(
  challenge: CognitoChallengeResponse,
  raw: Record<string, unknown>
): AuthChallengeResult {
  const session = challenge.Session ?? raw.Session ?? raw.session;
  const sessionValue = typeof session === 'string' ? session : undefined;

  return {
    success: false,
    challengeName: challenge.ChallengeName,
    challengeParameters: challenge.ChallengeParameters ?? {},
    ...(sessionValue !== undefined && { session: sessionValue }),
  };
}

function getAuthenticationResultRecord(
  payload: Record<string, unknown>
): Record<string, unknown> | null {
  const authResult = payload.AuthenticationResult;
  if (authResult == null || typeof authResult !== 'object' || Array.isArray(authResult)) {
    return null;
  }

  return authResult as Record<string, unknown>;
}

function pickTokenField(
  nested: Record<string, unknown> | null,
  payload: Record<string, unknown>,
  key: 'AccessToken' | 'IdToken' | 'RefreshToken'
): unknown {
  return nested?.[key] ?? payload[key];
}

function readNonEmptyTrimmedString(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return undefined;
  }

  return value;
}

async function buildClientMetadata(
  clientMetadata?: CognitoClientMetadata
): Promise<CognitoClientMetadata | undefined> {
  const providedIp = readNonEmptyTrimmedString(clientMetadata?.ipAddress)?.trim();
  const ipAddress = providedIp ?? (await getClientIpAddress());
  const providedUserAgent = readNonEmptyTrimmedString(clientMetadata?.userAgent)?.trim();
  const runtimeUserAgent =
    typeof navigator !== 'undefined'
      ? readNonEmptyTrimmedString(navigator.userAgent)?.trim()
      : undefined;
  const userAgent = providedUserAgent ?? runtimeUserAgent;

  if (ipAddress === undefined && userAgent === undefined) {
    return undefined;
  }

  return {
    ...(ipAddress !== undefined && { ipAddress }),
    ...(userAgent !== undefined && { userAgent }),
  };
}

function buildUserContextData(
  username: string,
  clientId: string
): { EncodedData: string } | undefined {
  const normalizedUsername = username.trim();
  const normalizedUserPoolId = COGNITO_USER_POOL_ID.trim();
  if (!normalizedUsername || !normalizedUserPoolId || typeof window === 'undefined') {
    return undefined;
  }

  try {
    const userPool = new CognitoUserPool({
      UserPoolId: normalizedUserPoolId,
      ClientId: clientId,
    });
    const typedUserPool = userPool as unknown as {
      getUserContextData?: (value: string) => unknown;
    };
    const rawContext = typedUserPool.getUserContextData?.(normalizedUsername) as
      | string
      | { EncodedData?: unknown }
      | undefined;

    const encodedData =
      typeof rawContext === 'string'
        ? readNonEmptyTrimmedString(rawContext)?.trim()
        : readNonEmptyTrimmedString(rawContext?.EncodedData)?.trim();

    if (encodedData === undefined) {
      return undefined;
    }

    return { EncodedData: encodedData };
  } catch {
    return undefined;
  }
}

function requireAccessToken(candidate: unknown): string {
  const token = readNonEmptyTrimmedString(candidate);
  if (token === undefined) {
    throw new Error('Invalid response: no tokens in response');
  }

  return token;
}

function readExpiresInSeconds(candidate: unknown): number {
  if (typeof candidate === 'number' && Number.isFinite(candidate)) {
    return candidate;
  }

  return 3600;
}

function buildAuthSuccessResult(success: CognitoInitiateAuthSuccessResponse): AuthResult {
  const payload = success as unknown as Record<string, unknown>;
  const nested = getAuthenticationResultRecord(payload);
  const accessToken = requireAccessToken(pickTokenField(nested, payload, 'AccessToken'));
  const idToken = readNonEmptyTrimmedString(pickTokenField(nested, payload, 'IdToken'));
  const refreshToken = readNonEmptyTrimmedString(pickTokenField(nested, payload, 'RefreshToken'));
  const expiresIn = readExpiresInSeconds(nested?.ExpiresIn ?? payload.ExpiresIn);

  return {
    success: true,
    accessToken,
    expiresIn,
    ...(idToken !== undefined && { idToken }),
    ...(refreshToken !== undefined && { refreshToken }),
  };
}

/**
 * Initiates auth with username (email) and password.
 * Returns tokens on success, or challenge info if Cognito requires MFA/new password.
 */
export async function initiateAuth(
  username: string,
  password: string,
  clientMetadata?: CognitoClientMetadata
): Promise<InitiateAuthResult> {
  const clientId = getRequiredClientId();
  const resolvedClientMetadata = await buildClientMetadata(clientMetadata);
  const userContextData = buildUserContextData(username, clientId);

  const body: CognitoInitiateAuthRequest = {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: clientId,
    AuthParameters: {
      USERNAME: username.trim(),
      PASSWORD: password,
    },
    ...(resolvedClientMetadata !== undefined && { ClientMetadata: resolvedClientMetadata }),
    ...(userContextData !== undefined && { UserContextData: userContextData }),
  };

  const url = getCognitoUrl();
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': COGNITO_INITIATE_AUTH_TARGET,
      scope: AUTH_SCOPE,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const err = data as CognitoErrorResponse;
    const message = getCognitoErrorMessage(err, response, 'Authentication failed');
    throw new Error(message);
  }

  // Challenge required (e.g. MFA, NEW_PASSWORD_REQUIRED)
  if (isCognitoChallengeResponse(data)) {
    return buildAuthChallengeResult(data, data as unknown as Record<string, unknown>);
  }

  return buildAuthSuccessResult(data as CognitoInitiateAuthSuccessResponse);
}

export async function respondToAuthChallenge(
  session: string,
  username: string,
  code: string,
  clientMetadata?: CognitoClientMetadata
): Promise<AuthResult> {
  const clientId = getRequiredClientId();
  const resolvedClientMetadata = await buildClientMetadata(clientMetadata);
  const userContextData = buildUserContextData(username, clientId);

  const body: CognitoRespondToAuthChallengeRequest = {
    ClientId: clientId,
    ChallengeName: 'SOFTWARE_TOKEN_MFA', // Must match InitiateAuth challenge
    Session: session,
    ChallengeResponses: {
      USERNAME: username.trim(),
      SOFTWARE_TOKEN_MFA_CODE: code.trim(),
    },
    ...(resolvedClientMetadata !== undefined && { ClientMetadata: resolvedClientMetadata }),
    ...(userContextData !== undefined && { UserContextData: userContextData }),
  };

  const url = getCognitoUrl();
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': COGNITO_RESPOND_TO_AUTH_CHALLENGE_TARGET,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const err = data as CognitoErrorResponse;
    const message = getCognitoErrorMessage(err, response, 'Verification failed');
    throw new Error(message);
  }

  return buildAuthSuccessResult(data as CognitoInitiateAuthSuccessResponse);
}

export type RefreshAuthOptions = {
  clientMetadata?: CognitoClientMetadata;
  /**
   * When true, invalid refresh tokens throw without calling handleSessionExpired.
   * Used by boot-time session validation so it can terminate with the correct realm once.
   */
  skipExpireHandler?: boolean;
};

/**
 * Refreshes Cognito tokens with REFRESH_TOKEN_AUTH.
 * Returns fresh tokens when refresh succeeds.
 */
export async function refreshAuthWithRefreshToken(
  refreshToken: string,
  clientMetadataOrOptions?: CognitoClientMetadata | RefreshAuthOptions
): Promise<AuthResult> {
  const options: RefreshAuthOptions =
    clientMetadataOrOptions != null &&
    ('clientMetadata' in clientMetadataOrOptions || 'skipExpireHandler' in clientMetadataOrOptions)
      ? clientMetadataOrOptions
      : { clientMetadata: clientMetadataOrOptions as CognitoClientMetadata | undefined };

  const normalizedRefreshToken = readNonEmptyTrimmedString(refreshToken);
  if (!normalizedRefreshToken) {
    throw new Error('Refresh token is missing.');
  }

  const clientId = getRequiredClientId();
  const resolvedClientMetadata = await buildClientMetadata(options.clientMetadata);
  const body = {
    AuthFlow: COGNITO_REFRESH_AUTH,
    ClientId: clientId,
    AuthParameters: {
      REFRESH_TOKEN: normalizedRefreshToken,
    },
    ...(resolvedClientMetadata !== undefined && { ClientMetadata: resolvedClientMetadata }),
  };

  const url = getCognitoUrl();
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': COGNITO_INITIATE_AUTH_TARGET,
      scope: AUTH_SCOPE,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = data as CognitoErrorResponse;
    if (isInvalidRefreshTokenResponse(data) && !options.skipExpireHandler) {
      handleSessionExpired();
    }

    const message = getCognitoErrorMessage(err, response, 'Session refresh failed');
    throw new Error(message);
  }

  const result = buildAuthSuccessResult(data as CognitoInitiateAuthSuccessResponse);
  // Cognito commonly omits RefreshToken in refresh response, preserve the current one.
  if (!result.refreshToken) {
    return {
      ...result,
      refreshToken: normalizedRefreshToken,
    };
  }

  return result;
}
